'use client';

import { useEffect, useRef, useState, ReactNode } from 'react';
import styles from './HeroScrollWrapper.module.css';

interface HeroScrollWrapperProps {
  children: ReactNode;
}

const HeroScrollWrapper = ({ children }: HeroScrollWrapperProps) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [scrollProgress, setScrollProgress] = useState(0);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    const handleScroll = () => {
      if (rafRef.current) return;

      rafRef.current = requestAnimationFrame(() => {
        if (!wrapperRef.current) {
          rafRef.current = null;
          return;
        }

        const rect = wrapperRef.current.getBoundingClientRect();
        const viewportHeight = window.innerHeight;

        // Animation happens during the first 50% of scrolling through the hero
        const scrollEnd = viewportHeight * 0.5;

        // Current scroll position within the hero
        const scrolled = Math.max(0, -rect.top);

        // Calculate progress (0 to 1)
        const progress = Math.min(1, Math.max(0, scrolled / scrollEnd));

        setScrollProgress(progress);
        rafRef.current = null;
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, []);

  // Easing function for smoother animation
  const easeOutQuad = (t: number) => t * (2 - t);
  const easedProgress = easeOutQuad(scrollProgress);

  // Scale: 1 -> 0.92 (subtle zoom out)
  const scaleAmount = 0.08;
  const scale = 1 - (easedProgress * scaleAmount);

  // Border radius: 0 -> 28px
  const borderRadius = easedProgress * 28;

  // Horizontal margin: 0 -> 40px (creates the card inset effect)
  const marginX = easedProgress * 40;

  // Top margin: 0 -> 24px
  const marginTop = easedProgress * 24;

  return (
    <div
      ref={wrapperRef}
      className={styles.heroScrollWrapper}
      style={{
        '--scroll-progress': scrollProgress,
        '--eased-progress': easedProgress,
        '--hero-scale': scale,
        '--hero-border-radius': `${borderRadius}px`,
        '--hero-margin-x': `${marginX}px`,
        '--hero-margin-top': `${marginTop}px`,
      } as React.CSSProperties}
    >
      <div className={styles.heroContainer}>
        {children}
      </div>
    </div>
  );
};

export default HeroScrollWrapper;
